/**
 * TopLocationsCompact Component
 *
 * Compact version of top locations panel for the redesigned Live Activity Dashboard.
 * Shows the most active locations in a space-efficient format.
 *
 * Features:
 * - Top 5 most active locations
 * - Intensity color coding
 * - Clickable items to navigate
 * - Professional design for sidebar
 *
 * @author BizClick WMS
 * @version 2.0 - Compact redesign
 */

import React, { useMemo } from 'react';
import { MapPin, Award, ChevronRight, TrendingUp } from 'lucide-react';
import { LocationActivityEvent, ActivityType } from '../../../../types/locationActivity';
import { TimeWindow } from './ActivityCacheManager';

interface TopLocationsCompactProps {
  /** All events for calculations */
  events: LocationActivityEvent[];

  /** Time window for filtering events */
  timeWindow?: TimeWindow;

  /** Number of top locations to show */
  topCount?: number;

  /** Callback when user clicks on a location */
  onLocationClick?: (binCode: string) => void;
}

interface LocationScore {
  binCode: string;
  rackNumber: number;
  score: number;
  eventCount: number;
  lastEventTime: string;
}

/**
 * Activity type weights for intensity calculation
 */
const ACTIVITY_WEIGHTS: Record<ActivityType, number> = {
  [ActivityType.CorrectionOut]: 1.5,
  [ActivityType.CorrectionIn]: 1.5,
  [ActivityType.Pick]: 1.0,
  [ActivityType.PutAway]: 1.0,
  [ActivityType.Receiving]: 1.0,
  [ActivityType.StockTransfer]: 1.0,
  [ActivityType.Replenishment]: 0.8,
  [ActivityType.Transfer]: 0.5,
  [ActivityType.InternalMove]: 0.5,
  [ActivityType.Optimization]: 0.5,
  [ActivityType.Counting]: 0.3,
  [ActivityType.Reservation]: 0.2,
  [ActivityType.OpeningBalance]: 0.1
};

/**
 * Filter events by time window
 */
const filterEventsByTimeWindow = (
  events: LocationActivityEvent[],
  timeWindow: TimeWindow
): LocationActivityEvent[] => {
  const now = Date.now();
  let cutoffTime: number;

  switch (timeWindow) {
    case TimeWindow.Last1Hour:
      cutoffTime = now - 60 * 60 * 1000;
      break;
    case TimeWindow.Last4Hours:
      cutoffTime = now - 4 * 60 * 60 * 1000;
      break;
    case TimeWindow.Last8Hours:
      cutoffTime = now - 8 * 60 * 60 * 1000;
      break;
    case TimeWindow.Today:
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      cutoffTime = today.getTime();
      break;
    default:
      return events;
  }

  return events.filter(event => {
    const eventTime = new Date(event.timestamp).getTime();
    return eventTime >= cutoffTime;
  });
};

/**
 * Get intensity color based on rank
 */
const getRankColor = (index: number): { bg: string; text: string; border: string } => {
  switch (index) {
    case 0:
      return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' };
    case 1:
      return { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' };
    case 2:
      return { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300' };
    default:
      return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
  }
};

/**
 * Format time ago
 */
const formatTimeAgo = (timestamp: string): string => {
  const now = Date.now();
  const eventTime = new Date(timestamp).getTime();
  const diffMs = now - eventTime;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Nå';
  if (diffMins < 60) return `${diffMins}m`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}t`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d`;
};

export const TopLocationsCompact: React.FC<TopLocationsCompactProps> = ({
  events,
  timeWindow = TimeWindow.Today,
  topCount = 5,
  onLocationClick
}) => {
  /**
   * Calculate top locations by activity intensity
   */
  const topLocations = useMemo((): LocationScore[] => {
    const filteredEvents = filterEventsByTimeWindow(events, timeWindow);

    // Group by binCode and calculate scores
    const locationScores = new Map<string, LocationScore>();

    filteredEvents.forEach(event => {
      const existing = locationScores.get(event.binCode);
      const eventScore = ACTIVITY_WEIGHTS[event.activityType] || 1.0;

      if (existing) {
        existing.score += eventScore;
        existing.eventCount++;
        if (new Date(event.timestamp) > new Date(existing.lastEventTime)) {
          existing.lastEventTime = event.timestamp;
        }
      } else {
        locationScores.set(event.binCode, {
          binCode: event.binCode,
          rackNumber: event.rackNumber,
          score: eventScore,
          eventCount: 1,
          lastEventTime: event.timestamp
        });
      }
    });

    return Array.from(locationScores.values())
      .sort((a, b) => b.score - a.score)
      .slice(0, topCount);
  }, [events, timeWindow, topCount]);

  const maxScore = topLocations.length > 0 ? topLocations[0].score : 1;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-transparent border-b border-gray-100">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-blue-500" />
          <h4 className="text-xs font-bold text-gray-600 uppercase tracking-wide">
            Top Lokasjoner
          </h4>
          <Award className="w-3.5 h-3.5 text-yellow-500" />
        </div>
      </div>

      {/* Locations List */}
      <div className="p-2">
        {topLocations.length === 0 ? (
          <div className="py-6 text-center text-gray-400">
            <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-xs">Ingen aktivitet</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {topLocations.map((location, index) => {
              const rankColor = getRankColor(index);
              const progressWidth = Math.round((location.score / maxScore) * 100);

              return (
                <button
                  key={location.binCode}
                  onClick={() => onLocationClick?.(location.binCode)}
                  className={`
                    w-full flex items-center gap-2 p-2 rounded-lg border
                    ${rankColor.border} ${rankColor.bg}
                    hover:shadow-sm transition-all duration-200
                    ${onLocationClick ? 'cursor-pointer' : 'cursor-default'}
                  `}
                >
                  {/* Rank Badge */}
                  <div className={`
                    w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                    ${index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-700' :
                      index === 2 ? 'bg-orange-300 text-orange-900' :
                      'bg-blue-200 text-blue-700'}
                  `}>
                    {index + 1}
                  </div>

                  {/* Location Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-bold ${rankColor.text}`}>
                        {location.binCode}
                      </span>
                      <span className="text-xs text-gray-500 font-medium">
                        {location.score.toFixed(1)}
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-300"
                        style={{ width: `${progressWidth}%` }}
                      />
                    </div>

                    {/* Meta */}
                    <div className="mt-1 flex items-center justify-between text-[10px] text-gray-500">
                      <span>{location.eventCount} hendelser</span>
                      <span>{formatTimeAgo(location.lastEventTime)}</span>
                    </div>
                  </div>

                  {/* Arrow */}
                  {onLocationClick && (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
        <p className="text-[10px] text-gray-400 text-center">
          Rangert etter aktivitetsintensitet
        </p>
      </div>
    </div>
  );
};

export default TopLocationsCompact;
